import {FC, useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useTheme, styled} from '@mui/material/styles'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import {useQueryClient} from 'react-query'
import {Placement} from '@popperjs/core'
import {ID, QUERIES, UserAvatar} from '../../../../../../../_metronic/helpers'
import {taskresponsible_adjust} from '../../TaskUsers/core/_requests'

export interface IconUserModel {
  name: string
  avatar?: string
  color?: string
  initials?: string
  task_responsible_id?: ID
  user_id: ID
}

type Props = {
  users: Array<IconUserModel>
  usersAll: Array<IconUserModel>
  classes?: string
  taskId?: ID
  isReadonly?: boolean
  placement?: Placement
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const {disablePortal, anchorEl, open, ...other} = props
  return <StyledAutocompletePopper {...other} />
}
//@ts-ignore
const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: '0px 0px 0px 3px rgba(181, 212, 78, 0.35)',
      borderColor: 'var(--tr-green-active)',
    },
  },
}))

const SubtaskUsersSymbolGroupColumn: FC<Props> = ({
  users,
  usersAll,
  classes,
  taskId,
  isReadonly = false,
  placement = 'top-end',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [value, setValue] = useState<IconUserModel[]>(usersAll)
  const [pendingValue, setPendingValue] = useState<IconUserModel[]>([])
  const theme = useTheme()
  const [localUsers, setLocalUsers] = useState(users)

  useEffect(() => {
    setLocalUsers(users)
  }, [users])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isReadonly) return

    localUsers?.map((user) => {
      if (user.task_responsible_id != null) {
        setPendingValue((value) => [...value, user])
      }
    })
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setValue(pendingValue)
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
  }

  const queryClient = useQueryClient()

  const open = Boolean(anchorEl)
  const intl = useIntl()

  return (
    <>
      <div className={`symbol symbol-group symbol-hover ${classes}`} onClick={handleClick}>
        {localUsers &&
          localUsers.map((user, i) => {
            return i < 3 ? (
              <OverlayTrigger
                key={`${i}-${user.name}`}
                placement='top'
                //@ts-ignore
                overlay={<Tooltip id='tooltip-user-name'>{user.name}</Tooltip>}
              >
                <UserAvatar
                  name={user?.name ?? ''}
                  avatar={user?.avatar}
                  sizeClass='symbol-25px'
                  imgClassName='object-fit-cover'
                  style={{cursor: isReadonly ? 'default' : 'pointer'}}
                />
              </OverlayTrigger>
            ) : null
          })}

        {localUsers && localUsers.length > 3 ? (
          <div className='symbol symbol-25px symbol-circle'>
            <span className='symbol-label bg-primary text-inverse-primary fw-bolder'>
              {localUsers.length}
            </span>
          </div>
        ) : null}

        {!isReadonly && (
          <div className='symbol symbol-25px symbol-circle'>
            <span className='symbol-label bg-primary text-inverse-primary fw-bolder'>+</span>
          </div>
        )}
      </div>
      {/* 
      //@ts-ignore */}
      <StyledPopper
        id={value?.toString()}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        disablePortal={true}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
                padding: '8px 10px',
                fontWeight: 600,
              }}
            >
              {intl.formatMessage({id: 'FORM.INPUT.NAME.ADDUSER_TASK'})}
            </Box>
            <Autocomplete
              open
              multiple
              onClose={(event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleClose()
                }
              }}
              value={pendingValue}
              onChange={async (event, newValue, reason, e) => {
                if (
                  event.type === 'keydown' &&
                  (event as React.KeyboardEvent).key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return
                }

                const exists = localUsers?.some(
                  // @ts-ignore
                  (user) => user.user_id == newValue[0].user_id || user.id == newValue[0].user_id
                )

                let updatedUsers = [] as IconUserModel[]

                if (exists) {
                  updatedUsers = localUsers?.filter((user) =>
                    // @ts-ignore
                    user.id ? user.id != newValue[0].user_id : user.user_id != newValue[0].user_id
                  )
                } else {
                  updatedUsers = [...localUsers ?? [], ...newValue]
                }

                setLocalUsers(updatedUsers)

                taskresponsible_adjust({
                  user_id: e?.option.user_id,
                  task_id: taskId,
                }).then(() => {
                  // Invalidar listagens de tarefas (não invalidar task-users nem usersTask = evita refetch getUsers)
                  queryClient.invalidateQueries({
                    predicate: (query) => {
                      // Listagens (table + kanban): tasks-list, mytasks-list, kanban (projects, sprints, mytasks, tasks-by-user)
                      const key = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey
                      if (typeof key !== 'string') return false
                      if (key === QUERIES.MYTASKS_LIST) return false
                      if (key.startsWith(`${QUERIES.TASKS_LIST}-usersTask`)) return false
                      if (key.startsWith(`${QUERIES.TASKS_LIST}-task-`)) return false
                      if (key.startsWith(`${QUERIES.TASKS_LIST}-project-`)) return false
                      if (key.startsWith(QUERIES.TASKS_LIST) && key.endsWith('-users')) return false
                      if (key.startsWith(QUERIES.TASKS_LIST) || key.startsWith(QUERIES.MYTASKS_LIST)) return true
                      if (key.startsWith('project-tasks-kanban')) return true
                      if (key === 'tasks') return true
                      if (key.startsWith(QUERIES.SPRINTS_LIST)) return true
                      if (key.startsWith('mytasks-kanban')) return true
                      if (key.startsWith('tasks-by-user-kanban')) return true
                      return false
                    },
                    refetchInactive: false,
                  })
                })
              }}
              disableCloseOnSelect
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText='No labels'
              renderOption={(props, option, {selected}) => (
                <li {...props}>
                  {localUsers?.some((user) =>
                    // @ts-ignore
                    user.user_id ? user.user_id === option.user_id : user.id === option.user_id
                  ) ? (
                    <Box
                      component={DoneIcon}
                      sx={{width: 17, height: 17, mr: '15px', ml: '-2px'}}
                      style={{
                        visibility: selected ? 'hidden' : 'visible',
                      }}
                    />
                  ) : (
                    <Box
                      component={DoneIcon}
                      sx={{width: 17, height: 17, mr: '15px', ml: '-2px'}}
                      style={{
                        visibility: selected ? 'visible' : 'hidden',
                      }}
                    />
                  )}
                  <Box sx={{mr: 1, mt: '2px', flexShrink: 0}}>
                    <UserAvatar
                      name={option?.name ?? ''}
                      avatar={option.avatar}
                      sizeClass='symbol-20px'
                      symbolLabelClass='me-2'
                      imgClassName='object-fit-cover'
                    />
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      '& span': {
                        color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                      },
                    }}
                  >
                    {option.name}
                    <br />
                  </Box>

                  {localUsers?.some((user) =>
                    // @ts-ignore
                    user.user_id ? user.user_id === option.user_id : user.id === option.user_id
                  ) ? (
                    <Box
                      component={CloseIcon}
                      sx={{opacity: 0.6, width: 18, height: 18}}
                      style={{
                        visibility: selected ? 'hidden' : 'visible',
                      }}
                    />
                  ) : (
                    <Box
                      component={CloseIcon}
                      sx={{opacity: 0.6, width: 18, height: 18}}
                      style={{
                        visibility: selected ? 'visible' : 'hidden',
                      }}
                    />
                  )}
                </li>
              )}
              options={
                usersAll?.sort((a, b) => {
                  // Display the selected labels first.
                  let ai = usersAll.indexOf(a)
                  ai = ai === -1 ? usersAll.length + usersAll.indexOf(a) : ai
                  let bi = usersAll.indexOf(b)
                  bi = bi === -1 ? usersAll.length + usersAll.indexOf(b) : bi
                  return ai - bi
                }) || []
              }
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.SELECTUSER_TEAMS'})}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}

export {SubtaskUsersSymbolGroupColumn}
